import { useState, useEffect, useRef } from 'react';
import { View, Text, Image, FlatList, TouchableOpacity, StyleSheet, Modal, Alert, ActivityIndicator, Platform, KeyboardAvoidingView, ScrollView, Keyboard } from 'react-native';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Notifications from 'expo-notifications';
import { useAuth } from '@/hooks/useAuth';
import { useWorkoutPlans } from '@/hooks/useWorkoutPlans';
import { useWorkoutSession } from '@/hooks/useWorkoutSession';
import { VideoPlayer, FullscreenVideoModal } from '@/components/VideoPlayer';
import { WorkoutLogForm } from '@/components/WorkoutLogForm';
import { ExerciseHistoryModal } from '@/components/ExerciseHistoryModal';
import { WorkoutFeedbackModal, FeedbackData } from '@/components/WorkoutFeedbackModal';
import { requestNotificationPermission } from '@/hooks/useNotifications';
import { supabase } from '@/lib/supabase';
import { trackEvent } from '@/lib/analytics';
import { PlanExercise } from '@/lib/types';

function VideoThumbnail({ thumbnailUri, onPress }: { thumbnailUri?: string | null; onPress: () => void }) {
  return (
    <View style={thumbStyles.wrapper}>
      <TouchableOpacity onPress={onPress} activeOpacity={0.85} style={thumbStyles.container}>
        {thumbnailUri
          ? <Image source={{ uri: thumbnailUri }} style={thumbStyles.image} resizeMode="cover" />
          : <View style={thumbStyles.placeholder}><Text style={thumbStyles.placeholderIcon}>🏋️</Text></View>
        }
        <View style={thumbStyles.overlay}>
          <View style={thumbStyles.playBtn}>
            <Text style={thumbStyles.playIcon}>▶</Text>
          </View>
        </View>
      </TouchableOpacity>
      <Text style={thumbStyles.demoLabel}>Ver demonstração</Text>
    </View>
  );
}

const thumbStyles = StyleSheet.create({
  wrapper: { alignItems: 'center', marginLeft: 12 },
  container: { width: 110, height: 125, borderRadius: 10, overflow: 'hidden', backgroundColor: '#111' },
  image: { width: 110, height: 125 },
  placeholder: { width: 110, height: 125, justifyContent: 'center', alignItems: 'center', backgroundColor: '#222' },
  placeholderIcon: { fontSize: 32 },
  overlay: { ...StyleSheet.absoluteFillObject, justifyContent: 'center', alignItems: 'center' },
  playBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.22)', borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.5)', justifyContent: 'center', alignItems: 'center' },
  playIcon: { color: '#fff', fontSize: 15, marginLeft: 3 },
  demoLabel: { fontSize: 9, color: '#C8102E', fontWeight: '600', marginTop: 3 },
});

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function WorkoutExecutionScreen() {
  const { planId } = useLocalSearchParams<{ planId: string }>();
  const { profile } = useAuth();
  const router = useRouter();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { planExercises, loading } = useWorkoutPlans({ planId });
  const { session, logs, sessionLoading, loadSession, startSession, logExercise, undoLog, completeSession, saveFeedback } = useWorkoutSession();
  const [studentId, setStudentId] = useState<string | null>(null);
  const [activeExercise, setActiveExercise] = useState<PlanExercise | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [confirmingFinish, setConfirmingFinish] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [completedSessionId, setCompletedSessionId] = useState<string | null>(null);
  const [lastWeights, setLastWeights] = useState<Record<string, number | null>>({});
  const [videoPreview, setVideoPreview] = useState<{ uri: string | null; name: string } | null>(null);
  const [historyExercise, setHistoryExercise] = useState<{ id: string; name: string } | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notifIdRef = useRef<string | null>(null);
  const schedulingRef = useRef(false);
  const logScrollRef = useRef<ScrollView>(null);
  const [personalId, setPersonalId] = useState<string | null>(null);
  const [studentName, setStudentName] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    supabase.from('students').select('id, personal_id, full_name').eq('profile_id', profile.id).single()
      .then(({ data }) => {
        if (data?.id) {
          setStudentId(data.id);
          setPersonalId(data.personal_id);
          setStudentName(data.full_name);
          loadSession(planId, data.id);
        }
      });
  }, [profile, planId]);

  useEffect(() => {
    supabase.from('workout_plans').select('name').eq('id', planId).single()
      .then(({ data }) => { if (data) navigation.setOptions({ title: data.name }); });
  }, [planId]);

  useEffect(() => {
    if (!studentId || planExercises.length === 0) return;

    async function fetchLastWeights() {
      // Build exercise_id → plan_exercise_id map for this plan
      const exerciseToCurrentPe: Record<string, string> = {};
      planExercises.forEach(pe => {
        const exId = (pe as any).exercise?.id;
        if (exId) exerciseToCurrentPe[exId] = pe.id;
      });
      const exerciseIds = Object.keys(exerciseToCurrentPe);
      if (!exerciseIds.length) return;

      // All plan_exercises across every plan that use the same exercises
      const { data: allPlanExercises } = await supabase
        .from('plan_exercises')
        .select('id, exercise_id')
        .in('exercise_id', exerciseIds);

      if (!allPlanExercises?.length) return;

      const allPeIds = allPlanExercises.map(pe => pe.id);
      const peToExercise: Record<string, string> = {};
      allPlanExercises.forEach(pe => { peToExercise[pe.id] = pe.exercise_id; });

      const { data: sessions } = await supabase
        .from('workout_sessions')
        .select('id')
        .eq('student_id', studentId!)
        .not('completed_at', 'is', null)
        .order('created_at', { ascending: false });

      if (!sessions?.length) return;

      const { data: logRows } = await supabase
        .from('session_logs')
        .select('plan_exercise_id, load_used, session_id')
        .in('session_id', sessions.map(s => s.id))
        .in('plan_exercise_id', allPeIds);

      if (!logRows?.length) return;

      const sessionOrder: Record<string, number> = {};
      sessions.forEach((s, i) => { sessionOrder[s.id] = i; });

      // Best load per exercise_id (most recent session)
      const best: Record<string, { order: number; load: number | null }> = {};
      logRows.forEach(row => {
        const exId = peToExercise[row.plan_exercise_id];
        if (!exId) return;
        const order = sessionOrder[row.session_id] ?? 999999;
        if (!(exId in best) || order < best[exId].order) {
          best[exId] = { order, load: row.load_used ?? null };
        }
      });

      // Map back to current plan's plan_exercise_id
      const map: Record<string, number | null> = {};
      Object.entries(best).forEach(([exId, { load }]) => {
        const peId = exerciseToCurrentPe[exId];
        if (peId) map[peId] = load;
      });
      setLastWeights(map);
    }

    fetchLastWeights();
  }, [studentId, planExercises]);

  useEffect(() => {
    if (!session || session.completed_at) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    const start = new Date(session.created_at).getTime();
    setElapsed(Math.floor((Date.now() - start) / 1000));
    intervalRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [session?.id, session?.completed_at]);

  // Quando o teclado abre no modal de registrar carga, rola pro fim pra revelar
  // o input + botão Salvar acima do teclado (Android não faz isso sozinho dentro de Modal).
  useEffect(() => {
    if (!activeExercise) return;
    const sub = Keyboard.addListener('keyboardDidShow', () => {
      logScrollRef.current?.scrollToEnd({ animated: true });
    });
    return () => sub.remove();
  }, [activeExercise]);

  async function handleStart() {
    if (!studentId) return;
    await startSession(planId, studentId);
    trackEvent('workout_started', { planId, totalExercises: planExercises.length });
    await scheduleAbandonedReminder();
  }

  async function scheduleAbandonedReminder() {
    if (Platform.OS === 'web') return;
    if (schedulingRef.current) return;
    schedulingRef.current = true;
    try {
      const granted = await requestNotificationPermission();
      if (!granted) return;
      if (notifIdRef.current) {
        await Notifications.cancelScheduledNotificationAsync(notifIdRef.current);
      }
      const id = await Notifications.scheduleNotificationAsync({
        content: {
          title: 'Não esqueça do seu treino! 💪',
          body: 'Hey! Parece que você esqueceu de concluir seu treino. Finalize agora para não perder o progresso!',
        },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
          seconds: 1800,
        },
      });
      notifIdRef.current = id;
    } finally {
      schedulingRef.current = false;
    }
  }

  async function cancelAbandonedReminder() {
    if (Platform.OS === 'web') return;
    if (notifIdRef.current) {
      await Notifications.cancelScheduledNotificationAsync(notifIdRef.current);
      notifIdRef.current = null;
    }
  }

  async function notifyPersonal(pId: string, sName: string) {
    try {
      const { data } = await supabase.from('profiles').select('expo_push_token').eq('id', pId).single();
      if (!data?.expo_push_token) return;
      await fetch('https://exp.host/--/api/v2/push/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: data.expo_push_token,
          title: 'Treino concluído ✅',
          body: `${sName} finalizou o treino`,
        }),
      });
    } catch {
      // fire-and-forget — never block the workout flow
    }
  }

  async function handleLog(result: { setsDone: number; repsDone: number; loadUsed: number | null }) {
    if (!session || !activeExercise) return;
    await logExercise(session.id, activeExercise.id, result.setsDone, result.repsDone, result.loadUsed);
    setLastWeights(prev => ({ ...prev, [activeExercise.id]: result.loadUsed }));
    setActiveExercise(null);
    await scheduleAbandonedReminder();
  }

  async function handleComplete() {
    if (!session) return;
    setConfirmingFinish(false);
    try { await cancelAbandonedReminder(); } catch { /* non-fatal */ }
    try {
      await completeSession(session.id);
      const pendingExercises = planExercises.length - Object.keys(logs).length;
      trackEvent(
        pendingExercises > 0 ? 'workout_completed_with_pending' : 'workout_completed',
        {
          planId,
          totalExercises: planExercises.length,
          loggedExercises: Object.keys(logs).length,
          pendingExercises,
          durationSec: elapsed,
        },
      );
      if (personalId && studentName) {
        notifyPersonal(personalId, studentName);
      }
      setCompletedSessionId(session.id);
      setShowFeedback(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      Alert.alert('Erro ao finalizar', msg);
    }
  }

  async function handleFeedbackDone(data?: FeedbackData) {
    if (data && completedSessionId) {
      try { await saveFeedback(completedSessionId, data); } catch { console.warn('[FeedbackModal] saveFeedback failed silently'); }
    }
    setShowFeedback(false);
    Alert.alert('Treino concluído! 🎉', `Tempo total: ${formatTime(elapsed)}`, [
      { text: 'OK', onPress: () => router.back() },
    ]);
  }

  if (loading || sessionLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  const allDone = Object.keys(logs).length === planExercises.length && planExercises.length > 0;
  const pending = planExercises.length - Object.keys(logs).length;

  return (
    <View style={styles.container}>
      <View style={styles.topBar}>
        <TouchableOpacity onPress={() => router.back()}>
          <Text style={styles.backBtnText}>← Voltar</Text>
        </TouchableOpacity>
        {session && !session.completed_at && (
          <View style={styles.timerBadge}>
            <Text style={styles.timerLabel}>TEMPO DE TREINO</Text>
            <Text style={styles.timerText}>{formatTime(elapsed)}</Text>
          </View>
        )}
      </View>

      {session?.completed_at ? (
        <View style={styles.doneContainer}>
          <Text style={styles.doneTitle}>Treino concluído!</Text>
          <Text style={styles.doneSub}>Ótimo trabalho! 💪</Text>
        </View>
      ) : (
        <>
          <FlatList
            data={planExercises}
            keyExtractor={pe => pe.id}
            renderItem={({ item }) => {
              const done = !!logs[item.id];
              return (
                <View style={[styles.exerciseRow, done && styles.exerciseDone]}>
                  <TouchableOpacity
                    style={styles.exerciseInfoArea}
                    onPress={() => session && !done && setActiveExercise(item)}
                    activeOpacity={session && !done ? 0.7 : 1}
                  >
                    <View style={styles.exerciseInfo}>
                      <Text style={styles.exerciseName}>{(item as any).exercise?.name}</Text>
                      <Text style={styles.exerciseMeta}>
                        {item.sets}x{item.reps_text ?? item.reps} • {
                          logs[item.id]?.load_used != null
                            ? `${logs[item.id].load_used}kg`
                            : lastWeights[item.id] != null
                            ? `${lastWeights[item.id]}kg`
                            : '0kg'
                        }
                      </Text>
                      {item.notes ? (
                        <Text style={styles.exerciseNotes}>{item.notes}</Text>
                      ) : null}
                      <TouchableOpacity
                        onPress={() => setHistoryExercise({ id: (item as any).exercise?.id, name: (item as any).exercise?.name ?? '' })}
                        style={{ marginTop: 4, alignSelf: 'flex-start' }}
                        hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                      >
                        <Text style={styles.historyLink}>📊 Ver progresso</Text>
                      </TouchableOpacity>
                      {session && done && (
                        <TouchableOpacity onPress={() => undoLog(item.id)} style={{ marginTop: 4 }}>
                          <Text style={styles.undoText}>↩ desfazer</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </TouchableOpacity>
                  <VideoThumbnail
                    thumbnailUri={(item as any).exercise?.thumbnail_url}
                    onPress={() => setVideoPreview({ uri: (item as any).exercise?.video_url ?? null, name: (item as any).exercise?.name ?? '' })}
                  />
                </View>
              );
            }}
            contentContainerStyle={{ padding: 16, paddingBottom: 120 + insets.bottom }}
          />

          <View style={[styles.footer, { paddingBottom: 16 + insets.bottom }]}>
            {!session ? (
              <TouchableOpacity style={styles.startButton} onPress={handleStart}>
                <Text style={styles.startButtonText}>Iniciar Treino</Text>
              </TouchableOpacity>
            ) : confirmingFinish ? (
              <View>
                <Text style={styles.confirmText}>
                  Ainda {pending === 1 ? 'há 1 atividade pendente' : `há ${pending} atividades pendentes`}. Tem certeza que quer finalizar?
                </Text>
                <View style={styles.confirmRow}>
                  <TouchableOpacity style={styles.cancelBtn} onPress={() => setConfirmingFinish(false)}>
                    <Text style={styles.cancelBtnText}>Não, continuar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.confirmBtn} onPress={handleComplete}>
                    <Text style={styles.confirmBtnText}>Finalizar</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <TouchableOpacity
                style={[styles.completeButton, !allDone && styles.completeButtonPartial]}
                onPress={allDone ? handleComplete : () => setConfirmingFinish(true)}
              >
                <Text style={styles.completeButtonText}>Finalizar Treino{allDone ? ' 🎉' : ''}</Text>
              </TouchableOpacity>
            )}
          </View>
        </>
      )}

      <FullscreenVideoModal
        key={videoPreview?.uri ?? 'placeholder'}
        uri={videoPreview?.uri}
        visible={!!videoPreview}
        onClose={() => setVideoPreview(null)}
      />

      <ExerciseHistoryModal
        exerciseId={historyExercise?.id ?? null}
        exerciseName={historyExercise?.name ?? ''}
        studentId={studentId ?? ''}
        onClose={() => setHistoryExercise(null)}
      />

      <Modal visible={!!activeExercise} animationType="slide" onRequestClose={() => setActiveExercise(null)}>
        {activeExercise && (
          <KeyboardAvoidingView
            style={styles.modal}
            behavior="padding"
          >
            <ScrollView
              ref={logScrollRef}
              contentContainerStyle={styles.modalScroll}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              <TouchableOpacity onPress={() => setActiveExercise(null)} style={styles.modalBackBtn}>
                <Text style={styles.modalBackBtnText}>← Voltar</Text>
              </TouchableOpacity>
              <VideoPlayer
                uri={(activeExercise as any).exercise?.video_url}
                thumbnailUri={(activeExercise as any).exercise?.thumbnail_url}
                /* Lazy by default: aluno so baixa video se clicar play. Economia ~80% de bandwidth. */
              />
              <Text style={styles.modalTitle}>{(activeExercise as any).exercise?.name}</Text>
              <Text style={styles.modalSub}>{(activeExercise as any).exercise?.muscle_group}</Text>
              {(activeExercise as any).exercise?.description && (
                <Text style={styles.modalDesc}>{(activeExercise as any).exercise.description}</Text>
              )}
              <Text style={styles.modalPrescription}>
                Prescrição: {activeExercise.sets} × {activeExercise.reps_text ?? activeExercise.reps}
              </Text>
              {activeExercise.notes ? (
                <Text style={styles.modalNotes}>📝 {activeExercise.notes}</Text>
              ) : null}
              <WorkoutLogForm sets={activeExercise.sets} reps={activeExercise.reps} suggestedLoad={lastWeights[activeExercise.id] ?? activeExercise.suggested_load} onSave={handleLog} />
            </ScrollView>
          </KeyboardAvoidingView>
        )}
      </Modal>

      <WorkoutFeedbackModal
        visible={showFeedback}
        onSave={handleFeedbackDone}
        onSkip={() => handleFeedbackDone()}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  topBar: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingTop: 14, paddingBottom: 4 },
  backBtnText: { color: '#C8102E', fontSize: 15, fontWeight: '600' },
  timerBadge: { backgroundColor: '#1A1A1A', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, alignItems: 'center' },
  timerLabel: { color: '#aaa', fontSize: 9, fontWeight: '600', letterSpacing: 0.5, marginBottom: 1 },
  timerText: { color: '#fff', fontSize: 15, fontWeight: '700', fontVariant: ['tabular-nums'] },
  doneContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  doneTitle: { fontSize: 26, fontWeight: 'bold', color: '#1A1A1A', marginBottom: 8 },
  doneSub: { fontSize: 16, color: '#888' },
  exerciseRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', padding: 12, borderRadius: 10, marginBottom: 8 },
  exerciseDone: { opacity: 0.5 },
  exerciseInfoArea: { flex: 1 },
  exerciseInfo: { flex: 1 },
  exerciseName: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  exerciseMeta: { fontSize: 13, color: '#888888', marginTop: 2 },
  exerciseNotes: { fontSize: 12, color: '#666666', marginTop: 3, fontStyle: 'italic' },
  undoText: { fontSize: 12, color: '#C8102E' },
  historyLink: { fontSize: 12 },
  footer: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: 16, backgroundColor: '#F5F5F5' },
  startButton: { backgroundColor: '#C8102E', padding: 16, borderRadius: 12, alignItems: 'center' },
  startButtonText: { color: '#fff', fontWeight: 'bold', fontSize: 16 },
  completeButton: { backgroundColor: '#2E7D32', padding: 16, borderRadius: 12, alignItems: 'center' },
  completeButtonPartial: { backgroundColor: '#888' },
  completeButtonText: { color: '#fff', fontWeight: 'bold', fontSize: 16 },
  confirmText: { fontSize: 13, color: '#555', textAlign: 'center', marginBottom: 10 },
  confirmRow: { flexDirection: 'row', gap: 10 },
  cancelBtn: { flex: 1, padding: 14, borderRadius: 12, borderWidth: 1.5, borderColor: '#ccc', alignItems: 'center' },
  cancelBtnText: { fontSize: 14, fontWeight: '600', color: '#555' },
  confirmBtn: { flex: 1, padding: 14, borderRadius: 12, backgroundColor: '#C8102E', alignItems: 'center' },
  confirmBtnText: { fontSize: 14, fontWeight: 'bold', color: '#fff' },
  videoModalBg: { flex: 1, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  videoModalCard: { backgroundColor: '#1A1A1A', borderRadius: 16, padding: 16, width: '100%', alignItems: 'center' },
  videoModalClose: { alignSelf: 'flex-end', padding: 4 },
  videoModalCloseText: { color: '#fff', fontSize: 18, fontWeight: '600' },
  videoModalTitle: { color: '#fff', fontSize: 16, fontWeight: '700', marginBottom: 14, alignSelf: 'flex-start' },
  modal: { flex: 1, backgroundColor: '#fff' },
  modalScroll: { padding: 20, paddingBottom: 40 },
  modalBackBtn: { marginBottom: 12 },
  modalBackBtnText: { color: '#C8102E', fontSize: 15, fontWeight: '600' },
  modalTitle: { fontSize: 22, fontWeight: 'bold', marginTop: 16, color: '#1A1A1A' },
  modalSub: { fontSize: 14, color: '#888888', marginTop: 4 },
  modalDesc: { fontSize: 15, color: '#444', marginTop: 8, marginBottom: 12 },
  modalPrescription: { fontSize: 15, fontWeight: '600', color: '#1A1A1A', marginTop: 10 },
  modalNotes: { fontSize: 14, color: '#444', marginTop: 8, backgroundColor: '#F5F5F5', padding: 10, borderRadius: 8 },
});
